import { useCallback, useEffect, useState, useMemo } from 'react'
import GrileService from '../../services/grile-service'
import TeacherService from 'services/teacher-service'
import { DIFFICULTIES_INTERVALS } from '../../constants/difficulties'
import { filterGridsByDifficulty, handleFilterByGrilaType } from '../../utils'
import useGetFilters from '../../hooks/useGetAllTags'

const useHistory = (create = false) => {
  const [filterOnMobile, setFilterOnMobile] = useState(0)
  const [filteredTags, setFilteredTags] = useState([])
  const [filteredCorrect, setFilteredCorrect] = useState([])
  const [filtersPopupOpened, setFiltersPopupOpened] = useState(false)
  const [grids, setGrids] = useState(undefined)
  const [subjects, setSubjects] = useState(undefined)
  const [savedGrids, setSavedGrids] = useState(undefined)
  const [isLoadingGrids, setIsLoadingGrids] = useState(false)
  const [allTags, setAllTags] = useState(undefined)
  const closeModal = () => setFiltersPopupOpened(false)
  const [hasSubscription, setHasSubscription] = useState(undefined)
  const [isNoteModalOpened, setIsNoteModalOpened] = useState(false)
  const [books, setBooks] = useState([])
  const [grileTypes, setGrileTypes] = useState([
    {
      id: 'single',
      name: 'Cu răspuns unic',
      checked: true,
    },
    {
      id: 'multiple',
      name: 'Cu răspuns multiplu',
      checked: true,
    },
  ])
  const [difficultyFilters, setDifficultyFilters] = useState([
    {
      key: 'easy',
      label: 'Usoară',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.EASY.minValue,
      maxValue: DIFFICULTIES_INTERVALS.EASY.maxValue,
    },
    {
      key: 'medium',
      label: 'Medie',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.MEDIUM.minValue,
      maxValue: DIFFICULTIES_INTERVALS.MEDIUM.maxValue,
    },
    {
      key: 'hard',
      label: 'Grea',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.HARD.minValue,
      maxValue: DIFFICULTIES_INTERVALS.HARD.maxValue,
    },
  ])
  const { coursesWithChapters } = useGetFilters()

  useEffect(() => {
    const fetcher = async () => {
      try {
        if (create) {
          const gridsResponse = await TeacherService.grile()
          setGrids(gridsResponse)
        } else if (filterOnMobile === 2) {
          const subjectsResponse = await GrileService.subjectHistory()
          setSubjects(subjectsResponse)
        } else if (filterOnMobile === 1) {
          const gridsResponse = await GrileService.history()
          const sortedAndFilteredGrids = gridsResponse?.grile.sort(
            (a, b) => b.timestamp - a.timestamp,
          )
          setGrids(sortedAndFilteredGrids)
          setBooks(gridsResponse?.books)
        } else if (filterOnMobile === 0) {
          const savedGridsResponse = await GrileService.getSavedGrids()
          setSavedGrids(savedGridsResponse.grile)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoadingGrids(false)
      }
    }

    if (
      (grids === undefined && filterOnMobile === 1) ||
      (!create && !subjects && filterOnMobile === 2) ||
      (filterOnMobile === 0 && savedGrids === undefined)
    ) {
      fetcher()
    } else {
      setIsLoadingGrids(false)
    }
  }, [filterOnMobile, create, grids, savedGrids, subjects])

  useEffect(() => {
    const fetcher = async () => {
      try {
        const response = await GrileService.getAllFilters()
        setAllTags(response['filters'])
        setHasSubscription(response['has_subscription'])
      } catch (error) {
        console.error(error)
      }
    }
    fetcher()
  }, [])

  const isLoading = useMemo(
    () =>
      isLoadingGrids || allTags === undefined || hasSubscription === undefined,
    [allTags, hasSubscription, isLoadingGrids],
  )

  const handleChange = useCallback((event, newValue) => {
    setIsLoadingGrids(true)
    setFilterOnMobile(newValue)
  }, [])

  const handleSelectDifficulty = useCallback(
    index => {
      const copy = [...difficultyFilters]
      // const isOnlyOneChecked =
      //   copy.filter(difficulty => difficulty.checked).length === 1

      // if (isOnlyOneChecked && copy[index].checked) {
      //   return
      // }

      copy[index].checked = !copy[index].checked
      setDifficultyFilters(copy)
    },
    [difficultyFilters],
  )
  const handleChangeGrileTypes = index => {
    if (grileTypes[index].id === 'single') {
      localStorage.setItem(
        'singleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    if (grileTypes[index].id === 'multiple') {
      localStorage.setItem(
        'multipleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    setGrileTypes(prevTypes =>
      prevTypes.map((type, i) =>
        i === index ? { ...type, checked: !type.checked } : type,
      ),
    )
  }

  const handleChangeChaptersByCourse = (course, isSelected) => {
    const newFilteredTags = [...filteredTags]
    if (isSelected) {
      for (let index = 0; index < coursesWithChapters[course].length; index++) {
        if (newFilteredTags.includes(coursesWithChapters[course][index].id)) {
          newFilteredTags.splice(
            newFilteredTags.indexOf(coursesWithChapters[course][index].id),
            1,
          )
        }
      }
    } else {
      for (let index = 0; index < coursesWithChapters[course].length; index++) {
        if (!newFilteredTags.includes(coursesWithChapters[course][index].id)) {
          newFilteredTags.push(coursesWithChapters[course][index].id)
        }
      }
    }
    setFilteredTags(newFilteredTags)
  }
  const handleUpdateNote = useCallback(
    async (gridId, note) => {
      try {
        await GrileService.updateNote({
          grila_id: gridId,
          note: note,
        })
        const savedGridsCopy = [...savedGrids]
        const gridIndex = savedGridsCopy.findIndex(grid => grid.id === gridId)
        savedGridsCopy[gridIndex].note = note
        setSavedGrids(savedGridsCopy)
      } catch (error) {
        console.error(error)
      }
    },
    [savedGrids],
  )

  const handleVerifyTagsInFilter = useCallback(
    grila => {
      for (let index = 0; index < grila.tags.length; index++) {
        if (filteredTags.includes(grila.tags[index])) {
          return true
        }
      }
      return false
    },
    [filteredTags],
  )

  const handleVerifyCorrectInFilter = useCallback(
    grila => {
      if (grila.choice === undefined || !filteredCorrect) {
        return true
      }

      if (filteredCorrect.includes(0) && filteredCorrect.includes(1)) {
        return true
      }

      if (filteredCorrect.includes(0)) {
        return grila.choice !== grila.correct_answer
      }

      if (filteredCorrect.includes(1)) {
        return grila.choice === grila.correct_answer
      }

      return false
    },
    [filteredCorrect],
  )

  const handleVerifyFilters = useCallback(
    grila => {
      return (
        handleVerifyTagsInFilter(grila) && handleVerifyCorrectInFilter(grila)
      )
    },
    [handleVerifyTagsInFilter, handleVerifyCorrectInFilter],
  )

  const combinedFilter = useCallback(
    grids => {
      if (grids === undefined) {
        return undefined
      }
      return filterGridsByDifficulty(
        handleFilterByGrilaType(grids, grileTypes),
        difficultyFilters,
      ).filter(grila => handleVerifyFilters(grila))
    },
    [handleVerifyFilters, grileTypes, difficultyFilters],
  )

  const filteredSavedGrids = useMemo(
    () => combinedFilter(savedGrids),
    [savedGrids, combinedFilter],
  )

  const filteredGrids = useMemo(
    () => combinedFilter(grids),
    [grids, combinedFilter],
  )

  // useEffect(() => {
  //   setGrids(combinedFilter(defaultGrids, grileTypes, difficultyFilters))
  //   setSavedGrids(
  //     combinedFilter(defaultSavedGrids, grileTypes, difficultyFilters),
  //   )
  // }, [difficultyFilters, grileTypes, defaultGrids, defaultSavedGrids])

  return {
    difficultyFilters,
    handleSelectDifficulty,
    filterOnMobile,
    handleChange,
    allTags,
    isLoading,
    grids,
    subjects,
    filteredCorrect,
    filteredTags,
    setFiltersPopupOpened,
    setFilteredCorrect,
    setFilteredTags,
    closeModal,
    filtersPopupOpened,
    setDifficultyFilters,
    hasSubscription,
    handleChangeChaptersByCourse,
    savedGrids,
    setSavedGrids,
    filteredGrids,
    filteredSavedGrids,
    isNoteModalOpened,
    setIsNoteModalOpened,
    handleUpdateNote,
    grileTypes,
    handleChangeGrileTypes,
    books,
  }
}

export default useHistory
